import {
    Control,
    ControlTypes,
    isNonValueType,
    isOption,
    Option,
} from '../../models';
import { IDatiqAction, IDatiqRulesData } from '@dat/iq-proxy';
import { computed, Signal, signal } from '@angular/core';
import {
    emptyTab,
    isTab,
    Tab,
} from '@dat/iq-control-tab';
import { emptyControl, emptyOption } from '../../models/empty-models';
import { isNumber } from 'lodash';

export type DashboardControlId = string;
export type ControlId = string;
export type ControlName = string;

export type DashboardControlProps = Record<
    DashboardControlId,
    DashControlController
>;
// export type ControlProps = Record<ControlId, Control>;
export type ControlProps = Record<ControlName, Control>;

export type DashboardOnSubmitProps = Record<
    DashboardControlId,
    IDatiqRulesData<IDatiqAction>[]
>;

export class DashControlController {
    private _controlMap: ControlProps = {};
    private _controlOrder: string[] = [];
    protected readonly _onSubmitActions = signal<DashboardOnSubmitProps>({});

    getControlByName(controlName: string) {
        return this._controlMap[controlName];
    }

    replaceControl(newControl: Control): this {
        this._controlMap[newControl.name] = { ...newControl };

        return this;
    }

    replaceAll(controls: Control[]): DashControlController {
        this._controlMap = controls.reduce((acc: ControlProps, control) => {
            return {
                ...acc,
                [control.name]: control,
            };
        }, {});
        this._controlOrder = controls.map((control: Control) => control.name);

        return this;
    }

    removeControl(controlName: string): DashControlController {
        this._controlMap = Object.keys(this._controlMap).reduce(
            (acc: ControlProps, name: string): ControlProps => {
                if (name === controlName) {
                    return acc;
                }

                return {
                    ...acc,
                    [name]: this._controlMap[name],
                };
            },
            {},
        );
        this._controlOrder = this._controlOrder.filter(
            (currentControlName) => currentControlName !== controlName,
        );

        return this;
    }

    setDashboardControls(value: Record<string, any>): DashControlController {
        if (!this._matchesAllNames(value)) {
            console.warn('setDashboardControls: not all names match', value);
            return this;
        }

        this._setValueByNameList(value);
        return this;
    }

    patchDashboardControls(value: Record<string, any>): DashControlController {
        this._setValueByNameList(value);
        return this;
    }

    setControl(controlId: string, control: Control): DashControlController {
        this._controlMap[controlId] = { ...control };
        return this;
    }

    removeControls(controlIds: string[]): this {
        controlIds.forEach((controlId) => {
            this.removeControl(controlId);
        });

        return this;
    }

    patchControl(
        controlId: string,
        value: Partial<Control>,
    ): DashControlController {
        const control: Control = this._controlMap[controlId] ?? emptyControl;
        const updatedControl: Control = { ...control, ...value } as Control;
        return this.setControl(controlId, updatedControl);
    }

    setOption(
        controlId: string,
        optionId: string,
        value: Option | Tab,
    ): DashControlController {
        const ctrl: Control = this._controlMap[controlId] ?? emptyControl;

        if (ctrl.type === 'Select' && isOption(value)) {
            const options: Option[] = (ctrl.options ?? []).map((option) => {
                return option.id === optionId ? { ...value } : option;
            });
            this._controlMap[controlId] = { ...ctrl, options };
        } else if (ctrl.type === 'Tabs' && isTab(value)) {
            const tabs: Tab[] = (ctrl.tabs ?? []).map((tab) => {
                return tab.id === optionId ? { ...value } : tab;
            });
            this._controlMap[controlId] = { ...ctrl, tabs };
        } else {
            console.warn(
                'setOption: control or option not found or value is not of type Tab or Option',
                controlId,
                optionId,
                value,
            );
        }

        return this;
    }

    patchOption(
        controlId: string,
        optionId: string,
        value: Partial<Option> | Partial<Tab>,
    ): DashControlController {
        const ctrl: Control = this._controlMap[controlId];

        if (ctrl && ctrl.type === 'Select') {
            const option: Option =
                (ctrl.options ?? []).find((option) => option.id === optionId) ??
                emptyOption;
            return this.setOption(controlId, optionId, { ...option, ...value });
        } else if (ctrl && ctrl.type === 'Tabs') {
            const tab: Tab =
                (ctrl.tabs ?? []).find((tab) => tab.id === optionId) ??
                emptyTab;
            return this.setOption(controlId, optionId, { ...tab, ...value });
        } else {
            console.warn(
                'setOption: control or option not found or value is not of type Tab or Option',
                controlId,
                optionId,
                value,
            );
        }

        return this;
    }

    disableControl(
        controlId: string,
        disabled: boolean,
    ): DashControlController {
        this._controlMap[controlId] = {
            ...this._controlMap[controlId],
            disabled,
        };
        return this;
    }

    disableOption(
        controlId: string,
        optionId: string,
        disabled: boolean,
    ): DashControlController {
        const ctrl: Control = this._controlMap[controlId] ?? emptyControl;

        if (ctrl.type === 'Select') {
            const updatedOptions: Option[] = ctrl.options.map((opt) => {
                return opt.id === optionId ? { ...opt, disabled } : opt;
            });
            this._controlMap[controlId] = { ...ctrl, options: updatedOptions };
        } else if (ctrl.type === 'Tabs') {
            const updatedTabs: Tab[] = ctrl.tabs.map((tab) => {
                return tab.id === optionId ? { ...tab, disabled } : tab;
            });
            this._controlMap[controlId] = { ...ctrl, tabs: updatedTabs };
        }

        return this;
    }

    addControl(
        control: Control,
        indexAt: number | undefined | null,
    ): DashControlController {
        const updatedControls: Control[] = [...this.getControls()];

        this.setControl(control.name, control);
        this._controlOrder = isNumber(indexAt)
            ? [
                  ...this._controlOrder.slice(0, indexAt),
                  control.name,
                  ...this._controlOrder.slice(indexAt),
              ]
            : [...this._controlOrder, control.name];

        indexAt !== undefined && indexAt !== null
            ? updatedControls.splice(indexAt, 0, control)
            : updatedControls.push(control);

        this.replaceAll(updatedControls);
        return this;
    }

    addAll(controls: Control[]): DashControlController {
        this._controlMap = controls.reduce((acc: ControlProps, control) => {
            return {
                ...acc,
                [control.name]: control,
            };
        }, this._controlMap);
        this._controlOrder = [
            ...this._controlOrder,
            ...controls.map((control: Control) => control.name),
        ];

        return this;
    }

    getControls(): Control[] {
        return this._controlOrder.map(
            (controlName: string) => this._controlMap[controlName],
        );
    }

    private _setValueByName(name: string, value: any): void {
        const oldControl: Control = this.getControls().find(
            (ctrl) => ctrl.name === name,
        );
        if (isNonValueType(oldControl)) {
            return;
        }

        const newControl: Control = {
            ...oldControl,
            value,
        };
        this.setControl(oldControl.name, newControl);
    }

    private _setValueByNameList(value: Record<string, any>): void {
        Object.entries(value).forEach(([key, value]) => {
            this._setValueByName(key, value);
        });
    }

    private _matchesAllNames(value: Record<string, any>): boolean {
        return this.getControls()
            .filter((ctrl) => !isNonValueType(ctrl))
            .every((ctrl) => Object.keys(value).indexOf(ctrl.name) >= 0);
    }
}
